/**
 * One documented request, written out in the language somebody is working in.
 *
 * Four renderings of the same call. They exist because the first thing anybody
 * does with an API reference is copy the example into a terminal or an editor,
 * and a `curl` line they have to translate first is a worse example than one
 * they can run.
 */
export const EXAMPLE_LANGUAGES = ['curl', 'http', 'javascript', 'python'] as const

export type ExampleLanguage = (typeof EXAMPLE_LANGUAGES)[number]

export interface RequestExample {
  /** Absent for a call with no method of its own to show, like a plain URL. */
  method?: string
  url: string
  /** The JSON body, or undefined for a request that carries none. */
  body?: unknown
  /** A file to attach, for the upload endpoints. */
  filename?: string
}

const TOKEN = 'YOUR_DATABASE_TOKEN'

export function formatJson(value: unknown): string {
  return JSON.stringify(value, null, 4)
}

function curl({ method, url, body, filename }: RequestExample): string {
  const lines = ['curl \\']
  if (method !== undefined) lines.push(`-X ${method} \\`)
  lines.push(`-H "Authorization: Token ${TOKEN}" \\`)
  if (body !== undefined) lines.push('-H "Content-Type: application/json" \\')
  if (filename !== undefined) lines.push(`-F file=@${filename} \\`)
  lines.push(body === undefined ? `"${url}"` : `"${url}" \\`)
  if (body !== undefined) lines.push(`--data '${formatJson(body)}'`)
  return lines.join('\n')
}

function http({ method, url, body, filename }: RequestExample): string {
  const lines = [`${method === undefined ? '' : `${method} `}${url} HTTP`]
  lines.push(`Authorization: Token ${TOKEN}`)
  if (filename !== undefined) {
    lines.push('Content-Length: YOUR_CONTENT_LENGTH')
    lines.push(
      'Content-Type: multipart/form-data; boundary=------------------------YOUR_BOUNDARY',
    )
  }
  if (body !== undefined) {
    lines.push('Content-Type: application/json')
    lines.push('')
    lines.push(formatJson(body))
  }
  return lines.join('\n')
}

function javascript({ method, url, body, filename }: RequestExample): string {
  const lines: string[] = []
  if (filename !== undefined) {
    lines.push('const formData = new FormData()')
    lines.push(`formData.append('file', '${filename}')`)
    lines.push('axios.post(url, formData, {')
  } else {
    lines.push('axios({')
  }
  if (method !== undefined) lines.push(`  method: "${method}",`)
  lines.push(`  url: "${url}",`)
  lines.push('  headers: {')
  const headers = [`    Authorization: "Token ${TOKEN}"`]
  if (filename !== undefined) headers.push('    "Content-Type": "multipart/form-data"')
  if (body !== undefined) headers.push('    "Content-Type": "application/json"')
  lines.push(headers.join(',\n'))
  lines.push(body === undefined ? '  }' : '  },')
  if (body !== undefined) {
    lines.push(`  data: ${indentAfterFirst(formatJson(body), '  ')}`)
  }
  lines.push('})')
  return lines.join('\n')
}

function python({ method, url, body, filename }: RequestExample): string {
  const lines = [`requests.${(method ?? 'GET').toLowerCase()}(`]
  lines.push(`    "${url}",`)
  lines.push('    headers={')
  const headers = [`        "Authorization": "Token ${TOKEN}"`]
  if (body !== undefined) headers.push('        "Content-Type": "application/json"')
  lines.push(headers.join(',\n'))
  lines.push(body === undefined && filename === undefined ? '    }' : '    },')
  if (filename !== undefined) {
    lines.push(
      `    files={"file": open("${filename}", "rb")}${body === undefined ? '' : ','}`,
    )
  }
  if (body !== undefined) {
    lines.push(`    json=${indentAfterFirst(formatJson(body), '    ')}`)
  }
  lines.push(')')
  return lines.join('\n')
}

/**
 * Re-indents a JSON block to sit inside the call it is an argument to.
 *
 * Everything but the first line, because the first is already positioned by the
 * text it was appended to.
 */
function indentAfterFirst(text: string, indent: string): string {
  return text.split('\n').join(`\n${indent}`)
}

const WRITERS: Record<ExampleLanguage, (example: RequestExample) => string> = {
  curl,
  http,
  javascript,
  python,
}

export function formatRequest(
  language: ExampleLanguage,
  example: RequestExample,
): string {
  return WRITERS[language](example)
}

/**
 * The other name for each field, against the line it appears on.
 *
 * Matched against the rendered text rather than counted forward from the top of
 * each example. Upstream walks the value and adds up a starting offset by hand
 * in every one of the four writers -- `let index = 3`, `= 2`, `= 5`, `= 5`,
 * incremented once per conditional branch -- so a header added to one of them
 * silently slides every annotation down by a line. Reading the key off the line
 * it is on cannot drift, because there is nothing to keep in step.
 *
 * A key inside an already-annotated value is left alone: a file object has a
 * `name` of its own, and a table with a column called Name would otherwise have
 * every attachment annotated as though it were that column.
 */
export function annotateLines(
  text: string,
  mapping: Record<string, string>,
): Record<number, string> {
  const annotations: Record<number, string> = {}
  let insideAnnotated: number | null = null

  text.split('\n').forEach((line, index) => {
    const match = /^(\s*)"([^"]+)":/.exec(line)
    if (match === null) return
    const indent = match[1]?.length ?? 0
    const key = match[2] ?? ''

    if (insideAnnotated !== null) {
      if (indent > insideAnnotated) return
      insideAnnotated = null
    }
    const other = mapping[key]
    if (other === undefined) return
    annotations[index] = other
    insideAnnotated = indent
  })

  return annotations
}
